import React, { FunctionComponent } from 'react'
import { useIntl } from 'react-intl'

import styled, { useTheme } from 'styled-components/native'

import { BodyText } from '../../atoms/bodyText/BodyText'
import { TargetIllustration } from '../../atoms/illustrations/TargetIllustration'
import { Title } from '../../atoms/title/Title'
import { TruncatedText } from '../../atoms/truncate/TruncatedText'
import { IS_WEB } from '../../constants'
import { BodyTextSize, TitleSize } from '../../styles/typeStyles'
import { Flex1View } from '../../templates/content/CommonViews'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

export interface GoalsCardProps {
  goal1: string
  goal2?: string
  goal3?: string
}

const BULLET_UNICODE = '\u25CF'
const BULLET_UNICODE_WITH_SPACE = '\u25CF   '

const GoalCardContent = styled.View<{ theme: ThemeType }>(({ theme: { colors, isAuroraTheme, spacing } }) => ({
  padding: spacing['16px'],
  paddingBottom: spacing['12px'],
  paddingRight: '80px',
  borderRadius: '16px',
  borderWidth: '1px',
  backgroundColor: isAuroraTheme ? colors.backgroundPrimary : colors.backgroundSection,
  borderColor: colors.borderDefault,
}))

const GoalInnerContent = styled.View({ flexDirection: 'row' })

const GoalCardDescription = styled(Flex1View)({
  justifyContent: 'center',
})

const TopContainer = styled.View({
  flexDirection: 'row',
})

const SubTitleContainer = styled.View({ flexDirection: 'row', marginTop: '4px' })

const BoxContainer = styled.View({
  display: 'block',
})

const BulletItemContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  display: 'block',
  flexDirection: 'row',
  paddingTop: spacing['4px'],
  paddingBottom: spacing['4px'],
}))

const BulletContainer = styled(BodyText)({
  display: 'block',
  fontSize: '10px',
  position: 'absolute',
  left: '0',
  top: '5px',
})

const TextContainer = styled(BodyText)<{ textColor?: string; theme: ThemeType }>(({ textColor, theme }) => ({
  color: textColor,
  paddingLeft: theme.spacing['12px'],
  display: 'block',
}))

const TargetIllustrationContainer = styled.View({
  position: 'absolute',
  bottom: 0,
  right: '14px',
})

export const GoalsCard: FunctionComponent<GoalsCardProps> = ({ goal1, goal2, goal3 }) => {
  const { formatMessage } = useIntl()
  const { colors, isAuroraTheme } = useTheme()

  // Truncated text on web can use more complex HTML than mobile so we can add
  // more formatting and it will still truncate properly
  const renderWebGoals = () => {
    return goal2 || goal3 ? (
      <BoxContainer>
        <BulletItemContainer>
          <BulletContainer size={BodyTextSize.CAPTION} color={colors.textSecondary} text={BULLET_UNICODE} />
          <TextContainer size={BodyTextSize.CAPTION} color={colors.textSecondary} text={goal1} />
        </BulletItemContainer>
        {goal2 && (
          <BulletItemContainer>
            <BulletContainer size={BodyTextSize.CAPTION} color={colors.textSecondary} text={BULLET_UNICODE} />
            <TextContainer size={BodyTextSize.CAPTION} color={colors.textSecondary} text={goal2} />
          </BulletItemContainer>
        )}
        {goal3 && (
          <BulletItemContainer>
            <BulletContainer size={BodyTextSize.CAPTION} color={colors.textSecondary} text={BULLET_UNICODE} />
            <TextContainer size={BodyTextSize.CAPTION} color={colors.textSecondary} text={goal3} />
          </BulletItemContainer>
        )}
      </BoxContainer>
    ) : (
      goal1
    )
  }

  // Truncated text on mobile devices can only be plain text,
  // so we render <BodyText> compoonents only
  const renderMobileGoals = () => {
    return goal2 || goal3 ? (
      <>
        <BulletContainer size={BodyTextSize.CAPTION} color={colors.textSecondary} text={BULLET_UNICODE_WITH_SPACE} />
        {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
        <TextContainer size={BodyTextSize.CAPTION} color={colors.textSecondary} text={goal1 + '\n'} />
        {goal2 && (
          <>
            <BulletContainer
              size={BodyTextSize.CAPTION}
              color={colors.textSecondary}
              text={BULLET_UNICODE_WITH_SPACE}
            />
            {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
            <TextContainer size={BodyTextSize.CAPTION} color={colors.textSecondary} text={goal2 + '\n'} />
          </>
        )}
        {goal3 && (
          <>
            <BulletContainer
              size={BodyTextSize.CAPTION}
              color={colors.textSecondary}
              text={BULLET_UNICODE_WITH_SPACE}
            />
            <TextContainer size={BodyTextSize.CAPTION} color={colors.textSecondary} text={goal3} />
          </>
        )}
      </>
    ) : (
      goal1
    )
  }

  return goal1.length > 0 ? (
    <GoalCardContent testID={tID('GoalCard-container')}>
      <GoalInnerContent>
        <GoalCardDescription defaultFlex>
          <TopContainer>
            <Title
              size={TitleSize.XSMALL}
              text={formatMessage({
                defaultMessage: 'Review your goals',
                description: 'Text describing user can review goals set by their provider',
              })}
            />
          </TopContainer>
          <SubTitleContainer>
            <TruncatedText
              text={IS_WEB ? renderWebGoals() : renderMobileGoals()}
              fontColor={colors.textSecondary}
              maxNumberOfLines={3}
              textSize={BodyTextSize.SMALL}
              collapseExpandText={[
                formatMessage({
                  defaultMessage: 'Less',
                  description: 'Text describing user can show less text by clicking the link',
                }),
                formatMessage({
                  defaultMessage: 'More',
                  description: 'Text describing user can show more text by clicking the link',
                }),
              ]}
              toggleCollapseExpand
              underline
              collapseExpandColor={colors.textActive}
              collapseExpandFontWeight='normal'
              collapseExpandTextSize={BodyTextSize.SMALL}
              textOverride={
                IS_WEB
                  ? goal1 + goal2 + goal3
                  : BULLET_UNICODE_WITH_SPACE +
                    goal1 +
                    '\n' +
                    (goal2 ? BULLET_UNICODE_WITH_SPACE + goal2 + '\n' : '') +
                    (goal3 ? BULLET_UNICODE_WITH_SPACE + goal3 : '')
              }
            />
          </SubTitleContainer>
        </GoalCardDescription>
      </GoalInnerContent>
      {!isAuroraTheme && (
        <TargetIllustrationContainer>
          <TargetIllustration />
        </TargetIllustrationContainer>
      )}
    </GoalCardContent>
  ) : null
}
